// packages/ui/src/hooks/usePermission.ts
//
// `usePermission(key)` — boolean predicate hook that reads the cached
// permissions slice from `useAuth` and matches against the colon-notation
// permission catalog (Amendment #2).
//
// The wildcard match algorithm mirrors the server-side helper in
// `apps/api/src/modules/auth/middlewares/permission.middleware.ts`
// (`permissionMatches`) — kept inline here per Task 42 brief constraint
// "Inline simple match for FE" so this hook does not pull a Fastify
// dependency through `@printway/shared`.
//
// Examples:
//   grant `role:*`        + required `role:create` → MATCH
//   grant `*:*`           + required `wallet:withdraw` → MATCH
//   grant `role:create`   + required `role:edit`   → NO MATCH

import { useAuth } from './useAuth.js';

/**
 * Returns true iff `grant` covers `required` per colon-segmented wildcard
 * rules. Mirrors `apps/api/.../permission.middleware.ts#permissionMatches`.
 */
export function permissionMatches(grant: string, required: string): boolean {
  const grantParts = grant.split(':');
  const reqParts = required.split(':');
  if (grantParts.length > reqParts.length) return false;

  for (let i = 0; i < grantParts.length; i++) {
    const g = grantParts[i];
    if (g === '*') return true; // wildcard — matches the rest of required.
    if (g !== reqParts[i]) return false;
  }
  return grantParts.length === reqParts.length;
}

// Migration alias map (P4.8 BLOCK-1): legacy bare permission names that
// predate the colon-notation migration (P1.D-08 / Amendment #2). The role
// seed still grants the legacy bare name for these, while the FE catalog
// (lib/permissions.ts) already uses the colon form. During the transition a
// granted legacy name satisfies the required colon-form key. Drop an entry
// once the seed grants the colon form for that permission.
const LEGACY_PERMISSION_ALIASES: Record<string, string> = {
  'shipping:manage': 'MANAGE_SHIPPING',
};

/**
 * True iff the granted `permissions` cover `key` — either directly (colon
 * wildcard match) or via a migration alias (legacy bare grant satisfying a
 * colon-form requirement). Shared by all three permission hooks so the alias
 * shim applies uniformly.
 */
function holdsPermission(permissions: string[], key: string): boolean {
  if (permissions.some((grant) => permissionMatches(grant, key))) return true;
  const alias = LEGACY_PERMISSION_ALIASES[key];
  return (
    alias !== undefined &&
    permissions.some((grant) => permissionMatches(grant, alias))
  );
}

/**
 * `usePermission('user:create')` — re-renders only when the permission
 * outcome changes (Zustand selector with shallow equality on boolean).
 *
 * Returns `false` while the auth store is loading or when no permissions
 * are present (consumer must handle the loading state via `useAuth().isLoading`
 * if they want a tri-state UI).
 */
export function usePermission(key: string): boolean {
  return useAuth((state) => holdsPermission(state.permissions, key));
}

/**
 * Variant — accept multiple keys, return true if user holds ANY of them.
 * Useful for OR-gated UI controls.
 */
export function useAnyPermission(keys: string[]): boolean {
  return useAuth((state) =>
    keys.some((key) => holdsPermission(state.permissions, key)),
  );
}

/**
 * Variant — return true only if user holds ALL listed keys.
 */
export function useAllPermissions(keys: string[]): boolean {
  return useAuth((state) =>
    keys.every((key) => holdsPermission(state.permissions, key)),
  );
}
